import { useState, useCallback } from 'react';
import { TerminalWindow } from './TerminalWindow';
import {
  SubsectionLabel,
  InfoPanel,
  Button,
  StepDotsNavigation,
} from './shared';
import type { SignalExplainerStep, SignalId } from '../content/demos/signalsDemo';
import {
  signalsDemoGlossary as SIGNALS,
  signalsDemoInteractive as sigInteractive,
  signalsExplainerSteps as EXPLAINER_STEPS,
  signalsVsKeys,
} from '../content/demos/signalsDemo';

type SignalInfo = (typeof SIGNALS)[number];

type ExplainerStep = SignalExplainerStep;

export function SignalsDemo() {
  const [selectedSignal, setSelectedSignal] = useState<SignalInfo>(SIGNALS[0]);
  const [terminalState, setTerminalState] = useState<
    'idle' | 'running' | 'interrupted' | 'suspended'
  >('idle');
  const [output, setOutput] = useState<string[]>([
    sigInteractive.terminalLines.idle,
  ]);
  const [currentStep, setCurrentStep] = useState<ExplainerStep>('what');

  const steps = Object.keys(EXPLAINER_STEPS) as ExplainerStep[];
  const stepContent = EXPLAINER_STEPS[currentStep];

  const simulateCommand = useCallback(() => {
    setTerminalState('running');
    setOutput([
      sigInteractive.terminalLines.sleeping,
      sigInteractive.terminalLines.sleepingMsg,
    ]);
  }, []);

  // Apply the effect of a signal to the terminal state
  const applySignalEffect = useCallback((signal: SignalId) => {
    if (signal === 'SIGINT') {
      setTerminalState('interrupted');
      setOutput((prev) => [
        ...prev,
        sigInteractive.simulatedOutput.interrupted,
        sigInteractive.terminalLines.idle,
      ]);
    } else if (signal === 'SIGTSTP') {
      setTerminalState('suspended');
      setOutput((prev) => [
        ...prev,
        sigInteractive.simulatedOutput.suspendedCtrlZ,
        sigInteractive.simulatedOutput.suspendedLine,
        sigInteractive.terminalLines.idle,
      ]);
    }
  }, []);

  const simulateSignal = useCallback(
    (signal: SignalId) => {
      if (terminalState !== 'running') {
        // Start a "command" first, then apply signal after delay
        simulateCommand();
        setTimeout(() => applySignalEffect(signal), 500);
      } else {
        applySignalEffect(signal);
      }
    },
    [terminalState, simulateCommand, applySignalEffect]
  );

  const resetTerminal = useCallback(() => {
    setTerminalState('idle');
    setOutput([sigInteractive.terminalLines.idle]);
  }, []);

  return (
    <div className="space-y-8">
      {/* Signal Glossary - side-by-side layout like VocabularyDemo */}
      <div className="bg-terminal-highlight border border-terminal-border flex flex-col lg:flex-row">
        {/* Signal list */}
        <div className="p-4 lg:border-r border-terminal-border lg:w-1/2">
          <div className="font-mono text-sm space-y-1">
            {SIGNALS.map((sig) => {
              const isActive = selectedSignal.key === sig.key;
              return (
                <div
                  key={sig.key}
                  onMouseEnter={() => setSelectedSignal(sig)}
                  className={`w-full text-left px-3 py-3 flex items-center justify-between transition-all cursor-default ${
                    isActive
                      ? 'bg-terminal-border/50 border-l-2 border-terminal-fg'
                      : 'border-l-2 border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <code className="text-terminal-yellow font-bold">
                      {sig.key}
                    </code>
                    <span className="text-terminal-fg">{sig.name}</span>
                  </div>
                  <span className="text-terminal-cyan text-sm">
                    {sig.signal}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Signal details */}
        <div className="p-4 lg:w-1/2 border-t lg:border-t-0 border-terminal-border">
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-3">
                <code className="text-terminal-yellow font-bold text-base">
                  {selectedSignal.key}
                </code>
                <span className="text-terminal-dim">→</span>
                <span className="text-terminal-green font-bold">
                  {selectedSignal.signal}
                </span>
              </div>
              <p className="text-terminal-muted text-xs mt-1">
                {selectedSignal.action}
              </p>
            </div>

            <p className="text-terminal-muted text-sm leading-relaxed">
              {selectedSignal.description}
            </p>

            <div className="bg-terminal-bg border border-terminal-border p-3 text-sm">
              <span className="text-terminal-dim">Example: </span>
              <span className="text-terminal-fg">{selectedSignal.example}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Terminal - now below the glossary */}
      <div className="space-y-4">
        <SubsectionLabel>{sigInteractive.tryIt}</SubsectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TerminalWindow>
            <div className="font-mono text-sm min-h-[180px]">
              {output.map((line, idx) => {
                const isLastLine = idx === output.length - 1;
                const showCursor =
                  isLastLine &&
                  (terminalState === 'idle' || terminalState === 'running');
                return (
                  <div
                    key={idx}
                    className={line.startsWith('^') ? 'text-terminal-red' : ''}
                  >
                    {line}
                    {showCursor && <span className="cursor-blink">█</span>}
                  </div>
                );
              })}
            </div>
          </TerminalWindow>

          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={simulateCommand}
                disabled={terminalState === 'running'}
              >
                {sigInteractive.runCommand}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => simulateSignal('SIGINT')}
              >
                Ctrl+C
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => simulateSignal('SIGTSTP')}
              >
                Ctrl+Z
              </Button>
              <Button size="sm" variant="secondary" onClick={resetTerminal}>
                {sigInteractive.reset}
              </Button>
            </div>

            {terminalState !== 'idle' && terminalState !== 'running' && (
              <div className="text-sm">
                <span className="text-terminal-dim">
                  {sigInteractive.statePrefix}
                </span>
                <span
                  className={
                    terminalState === 'interrupted'
                      ? 'text-terminal-red'
                      : 'text-terminal-yellow'
                  }
                >
                  {terminalState === 'interrupted' &&
                    sigInteractive.interrupted}
                  {terminalState === 'suspended' && sigInteractive.suspended}
                </span>
              </div>
            )}

            <p className="text-terminal-dim text-sm">{sigInteractive.hint}</p>
          </div>
        </div>
      </div>

      {/* Explainer */}
      <InfoPanel className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="text-terminal-red font-medium text-sm">
            {stepContent.title}
          </div>
          <StepDotsNavigation
            steps={steps}
            currentStep={currentStep}
            onStepChange={setCurrentStep}
          />
        </div>
        <p className="text-terminal-muted text-sm leading-relaxed">
          {stepContent.description}
        </p>
      </InfoPanel>

      {/* Signal vs Character Comparison */}
      <div className="bg-terminal-highlight border border-terminal-border p-6 space-y-4">
        <h3 className="text-terminal-red text-sm font-bold">
          {signalsVsKeys.heading}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-terminal-bg border border-terminal-border p-4 space-y-3">
            <div className="text-terminal-fg font-bold text-sm">
              {signalsVsKeys.regularHeading}
            </div>
            <div className="font-mono text-sm space-y-1">
              {signalsVsKeys.regularRows.map((row, i) => (
                <div key={i}>
                  <span className="text-terminal-yellow">{row.key}</span>
                  <span className="text-terminal-dim"> → </span>
                  <span className="text-terminal-cyan">{row.value}</span>
                  <span className="text-terminal-muted"> {row.note}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-terminal-bg border border-terminal-border p-4 space-y-3">
            <div className="text-terminal-fg font-bold text-sm">
              {signalsVsKeys.signalHeading}
            </div>
            <div className="font-mono text-sm space-y-1">
              <div>
                <span className="text-terminal-yellow">Ctrl+C</span>
                <span className="text-terminal-dim"> → </span>
                <span className="text-terminal-cyan">0x03</span>
                <span className="text-terminal-dim"> → </span>
                <span className="text-terminal-red">SIGINT</span>
              </div>
              <div>
                <span className="text-terminal-yellow">Ctrl+Z</span>
                <span className="text-terminal-dim"> → </span>
                <span className="text-terminal-cyan">0x1A</span>
                <span className="text-terminal-dim"> → </span>
                <span className="text-terminal-red">SIGTSTP</span>
              </div>
              <div className="text-terminal-dim text-xs mt-2">
                {signalsVsKeys.signalRowsFooter}
              </div>
            </div>
          </div>
        </div>

        <p className="text-terminal-dim text-sm">{signalsVsKeys.footer}</p>
      </div>
    </div>
  );
}
